export interface PollDiscussionArticle {
    idBrand: any;
    idArticle: any;
    title: string;
}

export interface FormattedPollForEdit {
    id: any;
    name: string;
    description: string;
    questions: Array<any>;
    sections: Array<any>;
    brandResults: Array<any>;
    idReferenceArticle: any;
    idReferenceBrand: any;
    referenceArticle: any;
    /** Of the brand in context only: an editor never sees another brand's discussion article. */
    discussionArticle: PollDiscussionArticle | null;
    dateGeneratedFromArticle: any;
    dateClosed: any;
    isWithTitle: boolean;
}

export interface FormattedPollForView {
    id: any;
    name: string;
    description: string;
    dateClosed: any;
    dateCreated: any;
    questions: Array<any>;
    brandResults: Array<any>;
    /** Of the brand in context only: the view screens never show another brand's discussion article. */
    discussionArticleTitle: string;
    idReferenceArticle: any;
    idReferenceBrand: any;
    referenceArticle: any;
    isWithTitle: boolean;
}

export class PollFormatter {
    public static formatForEdit(data: any, fallbackBrandId: any = null): FormattedPollForEdit {
        data = data || {};

        return {
            id: data.id || null,
            name: data.name || "",
            description: data.description || "",
            questions: Array.isArray(data.questions) ? data.questions : [],
            sections: Array.isArray(data.sections) ? data.sections : [],
            brandResults: Array.isArray(data.brandResults) ? data.brandResults : [],
            idReferenceArticle: data.idReferenceArticle || null,
            idReferenceBrand: data.idReferenceBrand || fallbackBrandId,
            referenceArticle: this.formatReferenceArticle(data, fallbackBrandId),
            discussionArticle: this.formatDiscussionArticle(data),
            dateGeneratedFromArticle: data.dateGeneratedFromArticle || null,
            dateClosed: data["dateClosed"] ? data["dateClosed"] : null,
            isWithTitle: !!data.isWithTitle,
        };
    }

    public static formatForView(data: any, fallbackBrandId: any = null): FormattedPollForView {
        data = data || {};
        const discussionArticle = this.formatDiscussionArticle(data);
        const formattedBrandResults = [];
        for (const brandResult of (Array.isArray(data.brandResults) ? data.brandResults : [])) {
            formattedBrandResults.push({
                ...brandResult,
                questions: this.formatChoices(brandResult.questions),
            });
        }

        return {
            id: data.id || null,
            name: data.name || "",
            description: data.description || "",
            dateClosed: data.dateClosed || null,
            dateCreated: data.dateCreated || "",
            questions: this.formatChoices(data.questions),
            brandResults: formattedBrandResults,
            discussionArticleTitle: discussionArticle ? discussionArticle.title : "",
            idReferenceArticle: data.idReferenceArticle || null,
            idReferenceBrand: data.idReferenceBrand || fallbackBrandId,
            referenceArticle: this.formatReferenceArticle(data, fallbackBrandId),
            isWithTitle: !!data.isWithTitle,
        };
    }

    public static formatChoices(questions: any): Array<any> {
        if (!Array.isArray(questions)) {
            return [];
        }

        return questions.map((question) => {
            const formattedQuestion = {
                ...question,
                choices: Array.isArray(question.choices) ? question.choices.map((choice) => ({...choice})) : [],
            };
            formattedQuestion.choices.forEach((choice) => {
                choice.percentage = formattedQuestion.votes > 0
                    ? Math.round((choice.votes / formattedQuestion.votes) * 100)
                    : 0;
            });

            return formattedQuestion;
        });
    }

    /**
     * A poll hosts its discussion on a different article on every brand it is attached to, so the
     * back end answers with the one belonging to the brand the request was made for.
     */
    public static formatDiscussionArticle(data: any): PollDiscussionArticle | null {
        const discussionArticle = data ? data.discussionArticle : null;
        if (!discussionArticle || !discussionArticle.idBrand || !discussionArticle.idArticle) {
            return null;
        }

        return {
            idBrand: discussionArticle.idBrand,
            idArticle: discussionArticle.idArticle,
            title: discussionArticle.title || `Article #${discussionArticle.idArticle}`,
        };
    }

    public static formatReferenceArticle(data: any, _fallbackBrandId: any = null): any {
        if (!data || !data.idReferenceArticle) {
            return null;
        }

        const referenceArticle = data.referenceArticle || {};
        const id = data.idReferenceArticle;
        return {
            ...referenceArticle,
            id,
            title: referenceArticle.title || referenceArticle.name || `Article #${id}`,
        };
    }
}



// WEBPACK FOOTER //
// ./js/utils/PollFormatter.ts